import { ActionsTab } from '@evidentid/tprm-portal-lib/models/entity-actions-review';

export const WRONG_ACTION_DETAILS_TYPE_MESSAGE = 'The actionReview object of this component has incorrect data, this is likely caused by mistake in the code.';

export const ACTIONS_TAB_QUERY_KEY = 'tab';

export const ACTIONS_TAB_LABELS: Record<ActionsTab, string> = {
    [ActionsTab.pending]: 'Pending',
    [ActionsTab.completed]: 'Completed',
};

/** Filters that only make sense once an action has been resolved. */
export const COMPLETED_ONLY_FILTER_KEYS: string[] = [
    'resolvedFrom',
    'resolvedTo',
    'resolvedBy',
    'outcome',
];

// The endpoint returns 409 for several distinct reasons and the response does not distinguish
// them in a form worth parsing, so this copy names the two an administrator can act on rather
// than asserting a single cause.
//
// The reachable ones are a concurrent revert -- another session reverted the same action between
// this modal opening and confirming -- and a deactivated owning entity, which the disabled state
// cannot anticipate: `revertable` is computed from the action's scope, status and resolution and
// never looks at whether the entity is still active, so the button is enabled for an action whose
// vendor was offboarded after it was resolved.
//
// An action completed before the audit table existed reaches this message like any other. It is
// revertable, so its modal opens and either cause can meet it there.
export const REVERT_CONFLICT_MESSAGE =
    'This action can no longer be reverted. It may already have been reverted, or its entity ' +
    'may no longer be active.';
export const REVERT_FORBIDDEN_MESSAGE = 'You do not have permission to revert this action.';
export const REVERT_NOT_REVERTABLE_MESSAGE = 'This action type cannot be reverted.';
export const REVERT_SUCCESS_MESSAGE = 'Action reverted. It returns to the queue if still required.';
// Closes the impact line in both variants, rather than standing as a sentence of its own.
export const REVERT_REQUEUE_SENTENCE = 'If the action is still required, it returns to the queue.';
// Both warnings state the same fact from the reader's side of it: nothing is holding notifications,
// so the re-evaluation can reach third parties. Shown only while that is true -- ticking the pause,
// or a tenant-wide hold, removes the notice rather than contradicting it.
export const REVERT_NOTIFICATION_WARNING_MANY =
    'These entities are not paused, so affected third parties may receive notifications.';
export const REVERT_NOTIFICATION_WARNING_ENTITY =
    'This entity is not paused, so the third party may receive notifications.';
export const REVERT_PAUSE_LABEL = 'Pause this entity';
export const REVERT_PAUSE_HINT =
    'Pausing only holds notifications — re-evaluation still runs. '
    + 'Unpause from the entity list when you\u2019re ready.';
// Appended to whichever specific failure the revert returned, when the pause before it did land. The
// specific message says why the revert failed; this says the entity is suppressed regardless.
export const REVERT_PAUSED_NOTE =
    'This entity was paused before the attempt, so unpause it from the entity list if you no longer '
    + 'want it paused.';
// Shown when the pause landed but the revert that followed did not: the entity is suppressed while its
// decision still stands, which the reviewer can only put right from another screen. Used when the
// failure carried no message of its own.
export const REVERT_FAILED_AFTER_PAUSE_MESSAGE =
    'This entity was paused, but the action could not be reverted, so its decision still stands. '
    + 'Try reverting again, or unpause the entity from the entity list if you no longer want it paused.';
// The pause is applied first and a failure stops the revert, so the decision still stands.
export const REVERT_PAUSE_FAILED_MESSAGE =
    'This entity could not be paused, so the action was not reverted and its decision still stands. '
    + 'Try again, or clear the pause option to revert without it.';
export const REVERT_ZERO_IMPACT_MESSAGE = 'No entities will be re-evaluated.';
export const REVERT_ZERO_IMPACT_EXPLANATION =
    'Nothing this decision applied to is still assigned, so reverting only withdraws the decision.';
export const REVERT_IMPACT_UNAVAILABLE_MESSAGE =
    'The impact could not be checked. Close this dialog and try again.';
export const RESOLUTION_UNAVAILABLE_MESSAGE = 'No resolution details were recorded.';
// Says what is missing without implying anything is blocked: these actions are revertable, the
// backend simply never stored who decided them or with what.
export const RESOLUTION_UNAVAILABLE_EXPLANATION =
    'This action was decided before resolution details were recorded. It can still be reverted.';
// Shown in place of a resolver on a resolution that carries only its timestamp.
export const RESOLVED_BY_UNRECORDED = 'Not recorded';

// The API's SortActionsBy enum is EnumEntry.LowerCase, so every sort column travels
// all-lowercase. Never hand-write a camelCase column.
export const ACTIONS_SORT_COLUMNS = {
    createdAt: 'createdat',
    completedAt: 'completedat',
    type: 'type',
    displayValue: 'displayvalue',
    entityName: 'entityname',
} as const;
